/**
 * Utilidades para posicionar y alinear texto en banners
 */

/**
 * Obtiene los estilos de alineación de texto basado en la configuración de Strapi
 * @param alineacion - "Izquierda" | "Centrado" | "Derecha"
 * @returns Objeto con estilos CSS para alineación
 */
export const getTextAlignment = (alineacion?: string | null): React.CSSProperties => {
  switch (alineacion) {
    case 'Centrado':
      return {
        textAlign: 'center',
        alignItems: 'center',
      };
    case 'Derecha':
      return {
        textAlign: 'right',
        alignItems: 'flex-end',
      };
    case 'Izquierda':
    default:
      return {
        textAlign: 'left',
        alignItems: 'flex-start',
      };
  }
};

/**
 * Obtiene los estilos de posición vertical del texto
 * @param posicion - "Superior" | "Centrado" | "Inferior"
 * @returns Objeto con estilos CSS para posición vertical
 */
export const getTextPosition = (posicion?: string | null): React.CSSProperties => {
  switch (posicion) {
    case 'Superior':
      return {
        justifyContent: 'flex-start',
        paddingTop: '32px',
      };
    case 'Inferior':
      return {
        justifyContent: 'flex-end',
        paddingBottom: '80px', // Espacio para el paginador
      };
    case 'Centrado':
    default:
      return {
        justifyContent: 'center',
      };
  }
};

/**
 * Obtiene la clase de fuente según el tipo
 * @param fuente - "Auto" | "Cursiva" | "Negrita" | "Medium"
 * @returns Nombre de la clase de fuente
 */
export const getFontClass = (fuente?: string | null): string => {
  switch (fuente) {
    case 'Auto':
      return 'font-tt-norms-regular';
    case 'Cursiva':
      return 'font-tt-norms-extra-black-italic';
    case 'Negrita':
      return 'font-tt-norms-extra-black';
    case 'Medium':
      return 'font-tt-norms-medium';
    default:
      return 'font-tt-norms-extra-black';
  }
};

/**
 * Devuelve el margen horizontal del contenedor de texto (mismo que padding izquierdo/derecho).
 * Útil para alinear el botón por cuadrante en cuadrantes impares.
 */
export const getBannerHorizontalSpacing = (posicion?: string | null): string => {
  if (posicion === 'Superior') return '32px';
  return '60px';
};

/** Margen horizontal del texto en banners móvil (padding izquierdo/derecho) */
export const getBannerHorizontalSpacingMobile = (): string => '24px';

/**
 * Obtiene los estilos completos para el contenedor de texto del banner (Desktop)
 * @param alineacion - Alineación horizontal
 * @param posicion - Posición vertical
 * @returns Objeto con estilos CSS combinados
 */
export const getBannerTextContainerStyles = (
  alineacion?: string | null,
  posicion?: string | null
): React.CSSProperties => {
  const alignmentStyles = getTextAlignment(alineacion);
  const positionStyles = getTextPosition(posicion);

  // Espaciado superior intacto; el mismo valor se aplica a izquierda y derecha
  const topSpacing = positionStyles.paddingTop as string | undefined;
  const horizontalSpacing = topSpacing ?? '60px';

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '0 60px',
    paddingLeft: horizontalSpacing,
    paddingRight: horizontalSpacing,
    zIndex: 5,
    ...positionStyles,
    ...alignmentStyles,
  };
};

/**
 * Obtiene los estilos completos para el contenedor de texto del banner (Mobile)
 * @param alineacion - Alineación horizontal
 * @param posicion - Posición vertical
 * @returns Objeto con estilos CSS combinados
 */
export const getBannerTextContainerStylesMobile = (
  alineacion?: string | null,
  posicion?: string | null
): React.CSSProperties => {
  const alignmentStyles = getTextAlignment(alineacion);
  const positionStyles = getTextPosition(posicion);

  // Ajustar padding para mobile según la posición
  let mobilePaddingTop = '0';
  let mobilePaddingBottom = '0';

  if (posicion === 'Superior') {
    mobilePaddingTop = '24px';
  } else if (posicion === 'Inferior') {
    mobilePaddingBottom = '60px';
  }

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingTop: mobilePaddingTop,
    paddingBottom: mobilePaddingBottom,
    zIndex: 5,
    justifyContent: positionStyles.justifyContent,
    ...alignmentStyles,
  };
};

